import React, { ReactNode, useMemo } from "react"
import { Grid } from "@mui/material"
import { DateTime } from "luxon"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef, MRT_FullScreenToggleButton, MRT_Row, MRT_TableInstance } from "material-react-table"
import { StandardExportButton } from "../table/StandardExportButton"
import { Subscription } from "../../../modules/merchant/accounting/accounting/accountingApi"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { useExportSubscriptionToCsv } from "../../../modules/merchant/merchant/production/components/ExportFunctionSubscription"
import { TableNumberOfItemsBox, TableSummaryWrap } from "../table/TableSummaryBox"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { StandardTwoLinesCell } from "../table/StandardTwoLinesCell"
import { useColumnFactory } from "../../../core/api/useTableColumns"
import { AmountUnit } from "../../../modules/currency/currencyTypes"
import { StandardToggleGlobalFilterButton } from "../table/StandardToggleGlobalFilterButton"
import { ApiQueryTable } from "../../../core/api/components/ApiQueryTable"
import { Box } from "@mui/system"
import { COLUMN_SIZE } from "../../constants/table/config"
import { StandardDateRangeFilter } from "../table/StandardDateRangeFilter"

const useStyles = makeStyles()(() => ({
    dateFilter: {
        width: "11rem",
    },
    table: {
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
}))

type DateState = Readonly<{
    startDate: DateTime
    endDate: DateTime
}>
type SetDateState = (update: DateState | ((prevState: DateState) => DateState)) => void

type SubscriptionsTableProps = Readonly<{
    tableData: Subscription[]
    tableState: { isLoading: boolean; isFetching: boolean; isError: boolean }
    rowActionMenuItems: (props: {
        closeMenu: () => void
        row: MRT_Row<Subscription>
        table: MRT_TableInstance<Subscription>
    }) => ReactNode[]
    toolbarInternalActions?: ReactNode
    topToolbarCustomActions?: (props: { table: MRT_TableInstance<Subscription> }) => ReactNode
    topToolbarElements?: ReactNode
    dateState: DateState
    setDateState: SetDateState
}>

export const SubscriptionsTable = ({
    tableData,
    tableState,
    rowActionMenuItems,
    topToolbarElements,
    toolbarInternalActions,
    topToolbarCustomActions,
    dateState,
    setDateState,
}: SubscriptionsTableProps) => {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const columnFactory = useColumnFactory<Subscription>()

    const columns = useMemo<MRT_ColumnDef<Subscription>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("accounting-subscription-title"),
                accessorKey: "name",
            }),

            columnFactory.nameColumn({
                header: l10n.getString("accounting-subscription-name"),
                accessorKey: "subscriptionDetails.customer.displayName",
                maxSize: COLUMN_SIZE.XL,
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={row.original?.subscriptionDetails.customer?.displayName ?? ""}
                        subtitle1={row.original.subscriptionDetails.customer?.email ?? ""}
                        subtitle2={row.original.subscriptionDetails.customer?.employeeId}
                    />
                ),
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("accounting-subscription-subscribed"),
                    accessorKey: "subscriptionDetails.validFrom",
                },
                (row) => row.subscriptionDetails.validFrom,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("accounting-subscription-unsubscribed"),
                    accessorKey: "subscriptionDetails.validTo",
                    size: COLUMN_SIZE.SM,
                    maxSize: COLUMN_SIZE.SM,
                },
                (row) => row.subscriptionDetails.validTo,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("accounting-subscription-payment-method"),
                id: "paymentMethod",
                accessorFn: (row) => {
                    const paymentMethod = row.subscriptionDetails?.paymentDetails?.method

                    if (paymentMethod) {
                        return l10n.getStringForEnum("purchases-payment-method", paymentMethod, paymentMethod)
                    }

                    return ""
                },
                Cell: ({ cell }) => {
                    return <p style={{ inlineSize: "8rem", overflowWrap: "break-word" }}>{cell.getValue<string>()}</p>
                },
            }),
            columnFactory.moneyColumnWithUnit(
                {
                    header: l10n.getString("accounting-subscription-amount"),
                    id: "subscriptionPrice",
                    Cell: ({ cell }) => {
                        return <span style={{ whiteSpace: "nowrap" }}>{cell.getValue<string>()}</span>
                    },
                },
                (row) => {
                    const orderLine = row.subscriptionDetails?.orderLines?.[0]
                    const { price } = orderLine ?? {}

                    return price
                },
                (row) => {
                    const orderLine = row.subscriptionDetails?.orderLines?.[0]
                    const { unit } = orderLine ?? {}

                    return unit ?? AmountUnit.MONTH
                },
            ),
        ],
        [columnFactory, l10n],
    )

    const exportSubscription = useExportSubscriptionToCsv({
        data: tableData,
        startDate: dateState.startDate,
        endDate: dateState.endDate,
    })

    return (
        <Box className={classes.table}>
            <ApiQueryTable
                isLoading={tableState.isLoading}
                isFetching={tableState.isFetching}
                isError={tableState.isError}
                columns={columns}
                data={tableData}
                enableClickToCopy={false}
                enableColumnOrdering
                enableStickyHeader
                enableDensityToggle={false}
                initialState={{ density: "spacious" }}
                enableRowActions
                enableGrouping
                renderRowActionMenuItems={rowActionMenuItems}
                renderToolbarInternalActions={({ table }) => (
                    <Grid container gap={1} paddingTop="1.5rem" wrap="nowrap">
                        {toolbarInternalActions ?? <></>}
                        <StandardExportButton
                            onClick={() => {
                                exportSubscription()
                            }}
                        />
                        <StandardToggleGlobalFilterButton table={table} />
                        <MRT_FullScreenToggleButton table={table} />
                    </Grid>
                )}
                renderTopToolbarCustomActions={
                    topToolbarCustomActions
                        ? topToolbarCustomActions
                        : () => (
                              <Grid container direction={"column"} width="100%">
                                  <Grid
                                      container
                                      wrap="nowrap"
                                      justifyContent="start"
                                      alignItems="center"
                                      paddingY="1rem"
                                      marginLeft="1rem"
                                      gap="2%"
                                  >
                                      <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                                      {topToolbarElements ?? <></>}
                                  </Grid>
                              </Grid>
                          )
                }
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap padding="sm">
                        <LocalizedStrict id="table-summary-box-number-of-subscriptions" attrs={{ title: true }}>
                            <TableNumberOfItemsBox
                                data={table.getFilteredRowModel().rows}
                                title="Number of subscriptions"
                            />
                        </LocalizedStrict>
                    </TableSummaryWrap>
                )}
            />
        </Box>
    )
}
